import { IAlarm } from '_/api/alarms/alarms.api';
import { IComment } from '_/common/types/comments.types';
import { IDelivery, IDeliveryStatsType, IDeliveryTransition } from '_/types/deliveries/delivery.types';
import { IDestinationPush } from '_/types/destinations/destinations.types';
import { ISnapshot } from '_/types/snapshots/snapshot.types';
import { ISource } from '_/types/sources/source.types';
import { formatNumber } from '_/utils/formatUtils';
import zeusSdk from '_/utils/SDKs';

export const fetchDelivery = async (deliveryId: string): Promise<IDelivery> => {
  try {
    const delivery = await zeusSdk.deliveries.get(deliveryId, {
      include: 'flow.project, flowdelivery, initiator',
    });
    return delivery;
  } catch (error) {
    console.error('Error fetching delivery', error);
    throw error;
  }
};

export const fetchDeliveryStats = async (deliveryId: string): Promise<IDeliveryStatsType | null> => {
  try {
    const stats = await zeusSdk.deliveries.getInfo(deliveryId);
    return stats;
  } catch (error) {
    console.error('Error fetching delivery stats', error);
    return null;
  }
};

export const fetchDeliveryComments = async (deliveryId: string): Promise<IComment[]> => {
  try {
    const options = {
      queryParameters: {
        limit: 1000,
        sort: 'addedAt',
        sortDirection: 'DESC',
      },
      include: 'editor',
    };
    const response = await zeusSdk.deliveries.listComments(deliveryId, options);
    return response?.items || [];
  } catch (error) {
    console.error('Error fetching delivery comments', error);
    return [];
  }
};

export const fetchDeliveryTransitions = async (deliveryId: string): Promise<IDeliveryTransition[]> => {
  try {
    const response = await zeusSdk.deliveries.getTransitions(deliveryId);
    return response?.items || [];
  } catch (error) {
    console.error('Error fetching delivery transitions', error);
    return [];
  }
};

export const createDeliveryComment = async (deliveryId: string, comment: string): Promise<IComment> => {
  return zeusSdk.deliveries.postComment(deliveryId, comment);
};

// Helper function to attach rollup stats to snapshots
// Based on the implementation in src/utils/snapshotUtils.js
const attachRollupStats = (snapshots: ISnapshot[], snapshotsRollup: any = {}): any[] => {
  return snapshots.map((snapshot) => {
    const health: any = {};
    const rollupHealth = snapshotsRollup[snapshot.id]?.health || {};
    const sumMetrics = ['rows', 'pages', 'failedOptionalTests', 'failedRequiredTests'];

    Object.entries(rollupHealth).forEach(([key, stats]: [string, any]) => {
      health[key] = sumMetrics.includes(key) ? formatNumber(stats.sum) : formatNumber(stats.weightedAvg);
    });

    return {
      ...snapshot,
      health: health,
    };
  });
};

export const fetchDeliveryTopLevelRollup = async (
  deliveryId: string,
  limit?: number | null,
  cursor?: string | null,
): Promise<{ items: any[]; cursor: string | null }> => {
  // Match the exact structure from the original getRootSnapshots
  const options: any = {
    filter: 'parentId||null',
    queryParameters: {
      limit: limit || 100,
      sort: 'addedAt',
      sortDirection: 'DESC',
    },
    include: 'Source.Collection.Project,importStatus,editor,assignedUser,parent',
  };

  if (cursor) {
    options.queryParameters.cursor = cursor;
  }

  try {
    // Fetch root snapshots
    const firstResults = await zeusSdk.deliveries.getChildren(deliveryId, 'snapshots', options);

    if (!firstResults || !firstResults.items || firstResults.items.length === 0) {
      return { items: [], cursor: null };
    }

    // Try to get rollup data for these snapshots
    try {
      const snapshotIds = firstResults.items.map((snapshot: any) => snapshot.id);

      // The rollup API returns an object indexed by snapshot IDs: { [snapshotId]: { health: {...} } }
      const firstRollup = await zeusSdk.snapshots.rollup(snapshotIds);

      const allSnapshots = attachRollupStats(firstResults.items, firstRollup);

      return {
        items: allSnapshots,
        cursor: firstResults.metadata?.cursor || null,
      };
    } catch (rollupError) {
      console.error('Error fetching rollup data, returning snapshots without stats:', rollupError);
      // Return snapshots without rollup data if rollup fails
      return {
        items: firstResults.items,
        cursor: firstResults.metadata?.cursor || null,
      };
    }
  } catch (error) {
    console.error('Error fetching delivery snapshots:', error);
    return { items: [], cursor: null };
  }
};

export const fetchDeliveryFilteredSnapshots = async (
  deliveryId: string,
  filter = 'status||in||PENDING_QA,QA,PASSED_QA,IN_PROGRESS,FAILED,FIXING,ESCALATED,FAILED_QA',
  limit = 100,
  cursor?: string | null,
): Promise<{ items: ISnapshot[]; cursor: string | null }> => {
  try {
    const options: any = {
      queryParameters: {
        limit: limit,
        filter: filter,
        sort: 'addedAt',
        sortDirection: 'DESC',
      },
      include: 'source.collection.project,importStatus,editor,assignedUser,parent',
    };

    if (cursor) {
      options.queryParameters.cursor = cursor;
    }

    const response = await zeusSdk.deliveries.getChildren(deliveryId, 'snapshots', options);

    return {
      items: response?.items || [],
      cursor: response?.metadata?.cursor || null,
    };
  } catch (error) {
    console.error('Error fetching filtered delivery snapshots', error);
    return { items: [], cursor: null };
  }
};

export const fetchDeliveryMetrics = async (deliveryId: string): Promise<Array<{ metric: string; data: any[] }>> => {
  try {
    // First, get the list of available metrics for this delivery
    const metrics = await zeusSdk.deliveries.listMetrics(deliveryId);

    // For each metric, fetch its data
    const promises = metrics.map((metric: string) =>
      zeusSdk.deliveries.getMetric(deliveryId, metric).then((data) => ({ metric: metric, data: data })),
    );

    const results = await Promise.allSettled(promises);

    // Filter out rejected promises and return only fulfilled ones
    return results
      .filter((result): result is PromiseFulfilledResult<{ metric: string; data: any[] }> => result.status === 'fulfilled')
      .map((result) => result.value);
  } catch (error) {
    console.error('Error fetching delivery metrics', error);
    return [];
  }
};

export const fetchDeliveryAlarms = async (deliveryId: string): Promise<any[]> => {
  try {
    const options = { include: 'Alarm.group,assignedUser' };
    const response = await zeusSdk.deliveries.getChildren(deliveryId, 'alarms', options);
    return response?.items || [];
  } catch (error) {
    console.error(`Error fetching delivery alarms for ${deliveryId}`, error);
    return [];
  }
};

export const updateDeliveryAlarm = async (deliveryAlarmId: string, deliveryAlarm: IAlarm): Promise<any> => {
  return zeusSdk.deliveryAlarms.patch(deliveryAlarmId, deliveryAlarm);
};

export const triggerDeliveryAlarm = async (deliveryAlarmId: string): Promise<any> => {
  return zeusSdk.deliveryAlarms.trigger(deliveryAlarmId, {});
};

export const resolveDeliveryAlarm = async (deliveryAlarmId: string): Promise<IAlarm> => {
  return zeusSdk.deliveryAlarms.resolve(deliveryAlarmId, {});
};

export const fetchDeliveryNotifications = async (deliveryId: string): Promise<any[]> => {
  try {
    const options = { include: 'integration,alarmgroup' };
    const response = await zeusSdk.deliveries.getChildren(deliveryId, 'notifications', options);
    return response?.items || [];
  } catch (error) {
    console.error(`Error fetching delivery notifications for ${deliveryId}`, error);
    return [];
  }
};

export const fetchDeliverySources = async (deliveryId: string): Promise<ISource[]> => {
  try {
    const options = {
      queryParameters: {
        limit: 1000,
        sort: 'addedAt',
        sortDirection: 'DESC',
      },
      include: 'source.collection',
    };

    const response = await zeusSdk.deliveries.getChildren(deliveryId, 'deliverysources', options);
    return response?.items || [];
  } catch (error) {
    console.error(`Error fetching delivery sources for ${deliveryId}`, error);
    return [];
  }
};

export const fetchDeliveryDestinationPushes = async (
  deliveryId: string,
  filter: string,
  limit = 50,
): Promise<{ items: IDestinationPush[]; cursor: string | null }> => {
  try {
    const options = {
      queryParameters: {
        limit: limit,
        filter: filter,
        sort: 'addedAt',
        sortDirection: 'DESC',
      },
      include: 'destination',
    };

    const response = await zeusSdk.deliveries.getDestinationPushes(deliveryId, options);
    return {
      items: response?.items || [],
      cursor: response?.metadata?.cursor || null,
    };
  } catch (error) {
    console.error(`Error fetching delivery destination pushes for ${deliveryId}`, error);
    return { items: [], cursor: null };
  }
};

/**
 * Update a delivery
 */
export const updateDelivery = async (deliveryId: string, delivery: IDelivery): Promise<any> => {
  return zeusSdk.deliveries.patch(deliveryId, delivery);
};

/**
 * Fetch organization deliveries with pagination
 */
export const fetchOrganizationDeliveries = async (
  orgId: string,
  filter?: string,
  cursor?: string | null,
): Promise<{ items: IDelivery[]; cursor: string | null }> => {
  try {
    const options: any = {
      queryParameters: {
        limit: 50,
        sort: 'addedAt',
        sortDirection: 'DESC',
        filter: filter || undefined,
      },
      include: 'flow.project',
    };

    if (cursor) {
      options.queryParameters.cursor = cursor;
    }

    const response = await zeusSdk.orgs.getChildren(orgId, 'deliveries', options);
    return {
      items: response?.items || [],
      cursor: response?.metadata?.cursor || null,
    };
  } catch (error) {
    console.error('Error fetching organization deliveries', error);
    return { items: [], cursor: null };
  }
};

/**
 * Cancel a delivery
 */
export const cancelDelivery = async (deliveryId: string): Promise<any> => {
  return zeusSdk.deliveries.cancel(deliveryId);
};
